import { useContext, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import axiosInstance from '../../lib/axios';
import { getUser } from '../../utils/auth';
import { palette, typography, theme } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimationStyles } from '../../hooks/useAnimations';

interface DailySalesData {
  date: string;
  sales: number;
  revenue: number;
  count: number;
}

interface TopProduct {
  productId: string;
  productName: string;
  quantity: number;
  revenue: number;
}

const fetchDailySalesAnalytics = async (startDate: Date, endDate: Date): Promise<DailySalesData[]> => {
  const user = getUser();
  const locationId = user?.locationId;
  const isAdmin = user?.isAdmin || false;

  if (!locationId && !isAdmin) {
    throw new Error('No store associated with user');
  }

  const params: Record<string, any> = {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };

  if (locationId) {
    params.locationId = locationId;
  }

  const response = await axiosInstance.get('/sales/analytics/daily', { params });

  return response.data;
};

const fetchTopProducts = async (startDate: Date, endDate: Date): Promise<TopProduct[]> => {
  const user = getUser();
  const locationId = user?.locationId;
  const isAdmin = user?.isAdmin || false;

  if (!locationId && !isAdmin) {
    throw new Error('No store associated with user');
  }

  const params: Record<string, any> = {
    limit: 10,
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };

  if (locationId) {
    params.locationId = locationId;
  }

  const response = await axiosInstance.get('/sales/analytics/top-products', { params });

  return response.data;
};

export const AnalyticsDashboard = () => {
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  // Date range state - default to last 30 days
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [selectedPreset, setSelectedPreset] = useState<string>('30d');

  const { data: dailySales, isLoading: loadingSales, error: salesError } = useQuery({
    queryKey: ['dailySalesAnalytics', startDate, endDate],
    queryFn: () => fetchDailySalesAnalytics(startDate, endDate),
  });

  const { data: topProducts, isLoading: loadingProducts, error: productsError } = useQuery({
    queryKey: ['topProducts', startDate, endDate],
    queryFn: () => fetchTopProducts(startDate, endDate),
  });

  const handlePresetClick = (preset: string) => {
    const end = new Date();
    const start = new Date();

    switch (preset) {
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
      case '1y':
        start.setFullYear(start.getFullYear() - 1);
        break;
    }

    setStartDate(start);
    setEndDate(end);
    setSelectedPreset(preset);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(new Date(e.target.value));
    setSelectedPreset('custom');
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(new Date(e.target.value));
    setSelectedPreset('custom');
  };

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const cardStyles = {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: theme.radius.xl,
    p: 3,
    transition: theme.transitions.normal,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-DZ', {
      month: 'short',
      day: 'numeric',
    });
  };

  if (loadingSales || loadingProducts) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: palette.primary }} />
      </Box>
    );
  }

  if (salesError || productsError) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          sx={{
            borderRadius: theme.radius.lg,
            backgroundColor: palette.errorMuted,
            color: palette.error,
          }}
        >
          {salesError instanceof Error ? salesError.message :
           productsError instanceof Error ? productsError.message :
           'Failed to load analytics data'}
        </Alert>
      </Box>
    );
  }

  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const getDaysRange = () => {
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Page Header */}
      <Box sx={{ mb: 3, ...animations.slideDown }}>
        <Typography
          variant="overline"
          sx={{
            color: palette.primary,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.xs,
            letterSpacing: typography.letterSpacing.widest,
            display: 'block',
            mb: 0.5,
          }}
        >
          Analytics
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: colors.textPrimary,
            mb: 0.5,
          }}
        >
          Sales Performance
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
          {getDaysRange()} days of insights and trends
        </Typography>
      </Box>

      {/* Date Range Picker */}
      <Box
        sx={{
          ...cardStyles,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayOutlinedIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
            }}
          >
            Time Period
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flex: 1, flexWrap: 'wrap' }}>
          <ButtonGroup
            variant="outlined"
            sx={{
              '& .MuiButton-root': {
                borderColor: colors.border,
                color: colors.textSecondary,
                textTransform: 'none',
                fontWeight: typography.fontWeight.medium,
                fontSize: typography.fontSize.sm,
                '&:hover': {
                  borderColor: palette.primary,
                  backgroundColor: palette.primaryMuted,
                },
              },
            }}
          >
            {['7d', '30d', '90d', '1y'].map((preset) => (
              <Button
                key={preset}
                onClick={() => handlePresetClick(preset)}
                sx={{
                  ...(selectedPreset === preset && {
                    backgroundColor: palette.primaryMuted,
                    borderColor: `${palette.primary} !important`,
                    color: palette.primary,
                  }),
                }}
              >
                {preset === '7d' && 'Last 7 days'}
                {preset === '30d' && 'Last 30 days'}
                {preset === '90d' && 'Last 90 days'}
                {preset === '1y' && 'Last year'}
              </Button>
            ))}
          </ButtonGroup>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              type="date"
              label="Start Date"
              value={formatDateForInput(startDate)}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.borderLight },
                  '&.Mui-focused fieldset': { borderColor: palette.primary },
                },
                '& .MuiInputLabel-root': { color: colors.textMuted },
                '& .MuiInputBase-input': { color: colors.textPrimary },
              }}
            />
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              to
            </Typography>
            <TextField
              type="date"
              label="End Date"
              value={formatDateForInput(endDate)}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.borderLight },
                  '&.Mui-focused fieldset': { borderColor: palette.primary },
                },
                '& .MuiInputLabel-root': { color: colors.textMuted },
                '& .MuiInputBase-input': { color: colors.textPrimary },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Sales Chart */}
      <Box sx={{ ...cardStyles, mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.semibold,
            color: colors.textPrimary,
            mb: 3,
          }}
        >
          Sales Overview
        </Typography>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart
            data={dailySales}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={palette.primary} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={palette.primary} stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={palette.success} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={palette.success} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
            <XAxis
              dataKey="date"
              stroke={colors.textMuted}
              tickFormatter={formatDate}
              style={{ fontSize: typography.fontSize.xs }}
            />
            <YAxis
              yAxisId="left"
              stroke={colors.textMuted}
              style={{ fontSize: typography.fontSize.xs }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke={colors.textMuted}
              style={{ fontSize: typography.fontSize.xs }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.md,
                color: colors.textPrimary,
              }}
              labelStyle={{ color: colors.textPrimary }}
              formatter={(value: any, name: string) => {
                if (name === 'revenue') return [formatCurrency(value), 'Revenue'];
                if (name === 'count') return [value, 'Sales Count'];
                return [value, name];
              }}
              labelFormatter={(label) => formatDate(label)}
            />
            <Legend
              wrapperStyle={{ color: colors.textPrimary }}
              formatter={(value) => {
                if (value === 'revenue') return 'Revenue';
                if (value === 'count') return 'Sales Count';
                return value;
              }}
            />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="revenue"
              stroke={palette.primary}
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorRevenue)"
            />
            <Area
              yAxisId="right"
              type="monotone"
              dataKey="count"
              stroke={palette.success}
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorCount)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      {/* Top Products Table */}
      <Box sx={cardStyles}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.semibold,
            color: colors.textPrimary,
            mb: 3,
          }}
        >
          Top Selling Products
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}>
                  Rank
                </TableCell>
                <TableCell sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}>
                  Product
                </TableCell>
                <TableCell align="right" sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}>
                  Quantity Sold
                </TableCell>
                <TableCell align="right" sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}>
                  Revenue
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topProducts && topProducts.length > 0 ? (
                topProducts.map((product, index) => (
                  <TableRow
                    key={product.productId}
                    sx={{
                      '&:hover': { backgroundColor: colors.surfaceElevated },
                      transition: theme.transitions.fast,
                    }}
                  >
                    <TableCell sx={{ color: colors.textPrimary }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: index < 3 ? palette.primaryMuted : colors.surfaceElevated,
                          color: index < 3 ? palette.primary : colors.textMuted,
                          fontWeight: typography.fontWeight.bold,
                        }}
                      >
                        {index + 1}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>
                      {product.productName}
                    </TableCell>
                    <TableCell align="right" sx={{ color: colors.textPrimary, fontFamily: typography.fontFamily.mono }}>
                      {product.quantity}
                    </TableCell>
                    <TableCell align="right" sx={{ color: palette.success, fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.semibold }}>
                      {formatCurrency(product.revenue)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: colors.textMuted }}>
                    No sales data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
